import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { Company, CurrentCompany, UserState } from "./user/types/userState.types";
import { initialUserState } from "./user/types/userState.types";

/**
 * companies = memberships (UsersCompany)
 * activeCompanyId = Company.id real
 * company = CurrentCompany derivada de membership.company + membership.userRole.role
 */
const pickActiveCompany = (
  companies: Company[],
  activeCompanyId: UserState["activeCompanyId"],
): CurrentCompany => {
  if (!companies?.length) return null;
  if (activeCompanyId == null) return null;

  const membership = companies.find((m) => String(m.companyId) === String(activeCompanyId));
  if (!membership?.company) return null;

  return {
    ...membership.company,
    coverageAreas: membership.company.coverageAreas ?? [],
    role: membership.userRole?.role,
  };
};

type UpdateUserPayload = Partial<UserState> & {
  companies?: Company[];
  activeCompanyId?: UserState["activeCompanyId"];
};

export const userStateSlice = createSlice({
  name: "userStateSlice",
  initialState: initialUserState,
  reducers: {
    updateUser: (state, action: PayloadAction<UpdateUserPayload>) => {
      const next = { ...state, ...action.payload };

      const companies = action.payload.companies ?? state.companies;
      const activeCompanyId = action.payload.activeCompanyId ?? state.activeCompanyId;

      if (action.payload.companies || action.payload.activeCompanyId) {
        next.company = pickActiveCompany(companies, activeCompanyId);
      }

      return next;
    },

    setCompanies: (state, action: PayloadAction<Company[]>) => {
      state.companies = action.payload ?? [];
      state.company = pickActiveCompany(state.companies, state.activeCompanyId);
    },

    setActiveCompany: (state, action: PayloadAction<UserState["activeCompanyId"]>) => {
      state.activeCompanyId = action.payload;
      state.company = pickActiveCompany(state.companies, state.activeCompanyId);
      sessionStorage.setItem("activeCompanyId", String(action.payload));
    },

    updateActiveCompany: (state, action: PayloadAction<Partial<CurrentCompany>>) => {
      if (!state.activeCompanyId || !state.companies.length) return;

      state.companies = state.companies.map((m) => {
        if (String(m.companyId) === String(state.activeCompanyId)) {
          return {
            ...m,
            company: {
              ...m.company!,
              ...action.payload,
            },
          } as Company;
        }
        return m;
      });

      state.company = pickActiveCompany(state.companies, state.activeCompanyId);
    },

    resetUser: () => initialUserState,
  },
});

export const {
  updateUser,
  setCompanies,
  setActiveCompany,
  updateActiveCompany,
  resetUser,
} = userStateSlice.actions;

export default userStateSlice.reducer;